import { useState, useEffect, useMemo } from 'react'
import { Zap, Search, Tag, ChevronDown, ChevronRight, Activity, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react'
const API_BASE = import.meta.env.VITE_API_URL || ''

type TabId = 'catalog' | 'health'

interface SkillEntry {
  name: string
  description: string
  version?: string
  tags: string[]
  triggers: string[]
  negativeTriggers: string[]
  priority: string
  category: string
  path: string
}

interface SkillCatalog {
  skills: SkillEntry[]
  totalCount: number
  categories: Record<string, number>
  scannedAt: string
}

interface SkillHealthEntry {
  skillName: string
  invocations7d: number
  invocations30d: number
  invocationsAll: number
  successRate7d: number | null
  successRate30d: number | null
  avgDurationMs30d: number | null
  avgTokenCost30d: number | null
  lastInvoked: string | null
  failures7d: number
  status: 'healthy' | 'degraded' | 'quarantined' | 'inactive'
  trend: 'improving' | 'stable' | 'degrading' | 'unknown'
}

interface SkillHealthReport {
  generatedAt: string
  totalSkills: number
  healthy: number
  degraded: number
  quarantined: number
  inactive: number
  skills: SkillHealthEntry[]
}

const CATEGORY_COLORS: Record<string, string> = {
  Document: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Export: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  'AI/Agent': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Development: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Database: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  Session: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  Skills: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  Workflow: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  General: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

// ---------------------------------------------------------------------------
// Status badge helpers
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<SkillHealthEntry['status'], { bg: string; icon: typeof CheckCircle }> = {
  healthy: { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle },
  degraded: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: AlertTriangle },
  quarantined: { bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
  inactive: { bg: 'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-500', icon: Clock },
}

const TREND_LABEL: Record<SkillHealthEntry['trend'], string> = {
  improving: 'Improving',
  stable: 'Stable',
  degrading: 'Degrading',
  unknown: '-',
}

const TREND_COLOR: Record<SkillHealthEntry['trend'], string> = {
  improving: 'text-green-600 dark:text-green-400',
  stable: 'text-gray-500 dark:text-gray-400',
  degrading: 'text-red-600 dark:text-red-400',
  unknown: 'text-gray-400 dark:text-gray-500',
}

function formatRate(rate: number | null): string {
  if (rate === null) return '-'
  return `${(rate * 100).toFixed(0)}%`
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '-'
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const diff = Date.now() - new Date(iso).getTime()
  const hours = diff / 3_600_000
  if (hours < 1) return 'just now'
  if (hours < 24) return `${Math.round(hours)}h ago`
  const days = hours / 24
  if (days < 7) return `${Math.round(days)}d ago`
  return `${Math.round(days / 7)}w ago`
}

// ---------------------------------------------------------------------------
// Health Tab Component
// ---------------------------------------------------------------------------

function SkillHealthTab() {
  const [report, setReport] = useState<SkillHealthReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<SkillHealthEntry['status'] | null>(null)

  useEffect(() => {
    fetch(`${API_BASE}/api/skills/health`, { credentials: 'include' })
      .then(r => r.ok ? r.json() as Promise<SkillHealthReport> : Promise.reject(r.status))
      .then(setReport)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    if (!report) return []
    if (!statusFilter) return report.skills
    return report.skills.filter(s => s.status === statusFilter)
  }, [report, statusFilter])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!report) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        Failed to load skill health data. Make sure the API server is running.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          { label: 'Healthy', count: report.healthy, color: 'text-green-600 dark:text-green-400', status: 'healthy' as const },
          { label: 'Degraded', count: report.degraded, color: 'text-amber-600 dark:text-amber-400', status: 'degraded' as const },
          { label: 'Quarantined', count: report.quarantined, color: 'text-red-600 dark:text-red-400', status: 'quarantined' as const },
          { label: 'Inactive', count: report.inactive, color: 'text-gray-500 dark:text-gray-400', status: 'inactive' as const },
        ]).map(card => (
          <button
            key={card.label}
            onClick={() => setStatusFilter(statusFilter === card.status ? null : card.status)}
            className={`bg-white dark:bg-white/5 border rounded-lg p-4 text-left transition-colors ${
              statusFilter === card.status
                ? 'border-blue-500 ring-1 ring-blue-500'
                : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20'
            }`}
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
            <p className={`text-2xl font-bold ${card.color}`}>{card.count}</p>
          </button>
        ))}
      </div>

      {/* Skills Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No skills with invocation data{statusFilter ? ` in "${statusFilter}" status` : ''}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-white/10">
                <th className="pb-2 font-medium">Skill</th>
                <th className="pb-2 font-medium text-center">Status</th>
                <th className="pb-2 font-medium text-center">7d</th>
                <th className="pb-2 font-medium text-center">30d</th>
                <th className="pb-2 font-medium text-center">Success (7d)</th>
                <th className="pb-2 font-medium text-center">Trend</th>
                <th className="pb-2 font-medium text-center">Avg Duration</th>
                <th className="pb-2 font-medium text-right">Last Used</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/5">
              {filtered.map(skill => {
                const style = STATUS_STYLES[skill.status]
                const Icon = style.icon
                return (
                  <tr key={skill.skillName} className="hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                    <td className="py-2.5">
                      <code className="text-sm font-medium text-gray-900 dark:text-white">/{skill.skillName}</code>
                      {skill.failures7d > 0 && (
                        <span className="ml-2 text-[10px] text-red-500">{skill.failures7d} fail</span>
                      )}
                    </td>
                    <td className="py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${style.bg}`}>
                        <Icon className="w-3 h-3" />
                        {skill.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-center text-gray-700 dark:text-gray-300">{skill.invocations7d}</td>
                    <td className="py-2.5 text-center text-gray-700 dark:text-gray-300">{skill.invocations30d}</td>
                    <td className="py-2.5 text-center">
                      <span className={skill.successRate7d !== null && skill.successRate7d < 0.8 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-700 dark:text-gray-300'}>
                        {formatRate(skill.successRate7d)}
                      </span>
                    </td>
                    <td className={`py-2.5 text-center text-xs ${TREND_COLOR[skill.trend]}`}>
                      {TREND_LABEL[skill.trend]}
                    </td>
                    <td className="py-2.5 text-center text-gray-500 dark:text-gray-400">
                      {formatDuration(skill.avgDurationMs30d)}
                    </td>
                    <td className="py-2.5 text-right text-gray-500 dark:text-gray-400 text-xs">
                      {formatRelativeTime(skill.lastInvoked)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <p className="text-xs text-gray-400 dark:text-gray-500">
        {report.totalSkills} skills tracked. Report generated {new Date(report.generatedAt).toLocaleString()}.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function SkillsCatalogPage() {
  const [catalog, setCatalog] = useState<SkillCatalog | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>('catalog')

  useEffect(() => {
    fetch(`${API_BASE}/api/skills/catalog`, { credentials: 'include' })
      .then(r => r.ok ? r.json() as Promise<SkillCatalog> : Promise.reject(r.status))
      .then(setCatalog)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    if (!catalog) return []
    let skills = catalog.skills
    if (selectedCategory) {
      skills = skills.filter(s => s.category === selectedCategory)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      skills = skills.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some(t => t.toLowerCase().includes(q))
      )
    }
    return skills
  }, [catalog, search, selectedCategory])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!catalog || !Array.isArray(catalog.skills)) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        Failed to load skills catalog. Make sure the API server is running.
      </div>
    )
  }

  const categories = Object.entries(catalog.categories ?? {}).sort((a, b) => b[1] - a[1])

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Skills Catalog</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {catalog.totalCount} skills available
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-white/10">
        {([
          { id: 'catalog' as TabId, label: 'Catalog', icon: Zap },
          { id: 'health' as TabId, label: 'Health', icon: Activity },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'health' && <SkillHealthTab />}

      {activeTab === 'catalog' && <>
      {/* Search + Category Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search skills by name, description, or tag..."
            className="w-full pl-10 pr-4 py-2 text-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              !selectedCategory
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
            }`}
          >
            All ({catalog.totalCount})
          </button>
          {categories.map(([cat, count]) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
              }`}
            >
              {cat} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Skills Grid */}
      <div className="grid gap-3">
        {filtered.map(skill => (
          <div
            key={skill.name}
            className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden hover:border-blue-300 dark:hover:border-blue-600/50 transition-colors"
          >
            <button
              onClick={() => setExpandedSkill(expandedSkill === skill.name ? null : skill.name)}
              className="w-full flex items-start gap-3 p-4 text-left"
            >
              <div className="flex-shrink-0 pt-0.5">
                {expandedSkill === skill.name
                  ? <ChevronDown className="w-4 h-4 text-gray-400" />
                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-sm font-semibold text-gray-900 dark:text-white">
                    /{skill.name}
                  </code>
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${CATEGORY_COLORS[skill.category] || CATEGORY_COLORS.General}`}>
                    {skill.category}
                  </span>
                  {skill.priority === 'high' && (
                    <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                      High Priority
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {skill.description}
                </p>
              </div>
            </button>

            {expandedSkill === skill.name && (
              <div className="px-4 pb-4 pl-11 space-y-3 border-t border-gray-100 dark:border-white/5 pt-3">
                {/* Tags */}
                {skill.tags.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Tag className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {skill.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-[10px] bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Triggers */}
                {skill.triggers.length > 0 && (
                  <div>
                    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-1">
                      Triggers
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {skill.triggers.map(t => (
                        <code key={t} className="px-2 py-0.5 text-[10px] bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded">
                          {t}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                {/* Version + Path */}
                <div className="flex items-center gap-4 text-[10px] text-gray-400 dark:text-gray-500">
                  {skill.version && <span>v{skill.version}</span>}
                  <span>{skill.path}</span>
                </div>
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No skills found matching your search.
          </div>
        )}
      </div>
      </>}
    </div>
  )
}
